import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { StompSubscription } from '@stomp/stompjs';
import RabbitStompService from '../services/rabbit-stomp/rabbit-stomp.service';
import { RabbitStompConfig } from '../services/rabbit-stomp/rabbit-stomp.types';
import { rabbitmqConfig } from '../config/rabbitmq';

interface RabbitStompContextData {
  isConnected: boolean;
  error: string | null;
  sendToQueue: (message: string) => Promise<void>;
  subscribeToQueue: (queueName: string, callback: (message: string) => void) => () => void;
  subscribeToTopic: (
    topicPattern: string,
    callback: (message: string, destination: string) => void
  ) => () => void;
  connect: () => void;
  disconnect: () => void;
}

const RabbitStompContext = createContext<RabbitStompContextData | null>(null);

interface RabbitStompProviderProps {
  children: React.ReactNode;
  config?: RabbitStompConfig;
  autoConnect?: boolean;
}

export const RabbitStompProvider: React.FC<RabbitStompProviderProps> = ({ 
  children, 
  config = rabbitmqConfig,
  autoConnect = false  // NÃO conectar automaticamente - deixar o ServicesInitializer gerenciar
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const serviceRef = useRef<RabbitStompService | null>(null);
  const subscriptionsRef = useRef<Map<string, StompSubscription>>(new Map());
  
  // Apenas observar a instância - NÃO criar nem conectar
  useEffect(() => {
    let syncInterval: ReturnType<typeof setInterval> | null = null;
    let waitForInstance: ReturnType<typeof setInterval> | null = null;
    let handleConnect: (() => void) | null = null;
    let handleDisconnect: (() => void) | null = null;
    let handleError: ((errorMsg: string) => void) | null = null;
    let lastInstance: RabbitStompService | null = null;
    
    // Função para registrar/re-registrar handlers
    const setupHandlers = (instance: RabbitStompService) => {
      // Remover handlers antigos se existirem
      if (lastInstance && handleConnect) {
        lastInstance.off('connect', handleConnect);
        lastInstance.off('disconnect', handleDisconnect!);
        lastInstance.off('error', handleError!);
      }
      
      // Criar novos handlers
      handleConnect = () => {
        setIsConnected(true);
        setError(null);
      };

      handleDisconnect = () => {
        setIsConnected(false);  
      };

      handleError = (errorMsg: string) => {
        setError(errorMsg);
        setIsConnected(false);
      };

      // Registrar handlers
      instance.on('connect', handleConnect);
      instance.on('disconnect', handleDisconnect);
      instance.on('error', handleError);

      // Verificar estado atual
      const currentState = instance.isConnected();
      setIsConnected(currentState);
      
      lastInstance = instance;
    };
    
    // Tentar obter instância existente (criada pelo ServicesInitializer)
    const tryGetInstance = () => {
      try {
        const instance = RabbitStompService.getInstance();
        
        // Se é uma instância diferente da anterior, re-configurar
        if (instance !== serviceRef.current) {
          serviceRef.current = instance;
          setupHandlers(instance);
        }
        
        return true;
      } catch (err) {
        return false;
      }
    };

    // Polling contínuo para detectar instância e mudanças
    waitForInstance = setInterval(() => {
      tryGetInstance();
      
      // Polling para sincronização contínua do estado
      if (serviceRef.current) {
        const state = serviceRef.current.isConnected();
        setIsConnected(state);
      }
    }, 500);

    // Cleanup
    return () => {
      if (waitForInstance) clearInterval(waitForInstance);
      if (syncInterval) clearInterval(syncInterval);
      if (serviceRef.current && handleConnect) {
        serviceRef.current.off('connect', handleConnect);
        serviceRef.current.off('disconnect', handleDisconnect!);
        serviceRef.current.off('error', handleError!);
      }
      
      const subscriptions = subscriptionsRef.current;
      const service = serviceRef.current;
      subscriptions.forEach((subscription) => {
        service?.unsubscribe(subscription);
      });
      subscriptions.clear();
    };
  }, []);

  const sendToQueue = useCallback(
    async (message: string): Promise<void> => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }
      return serviceRef.current.sendToQueue(message);
    },
    []
  );

  const subscribeToQueue = useCallback(
    (queueName: string, callback: (message: string) => void): (() => void) => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }

      const subscription = serviceRef.current.subscribeToQueue(queueName, callback);
      subscriptionsRef.current.set(queueName, subscription);

      // Retorna função para cancelar a inscrição
      return () => {
        serviceRef.current?.unsubscribe(subscription);
        subscriptionsRef.current.delete(queueName);
      };
    },
    []
  );

  const subscribeToTopic = useCallback(
    (
      topicPattern: string,
      callback: (message: string, destination: string) => void
    ): (() => void) => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }

      const subscription = serviceRef.current.subscribeToTopic(topicPattern, callback);
      const key = `topic_${topicPattern}`;
      subscriptionsRef.current.set(key, subscription);

      // Retorna função para cancelar a inscrição
      return () => {
        serviceRef.current?.unsubscribe(subscription);
        subscriptionsRef.current.delete(key);
      };
    },
    []
  );

  const connect = useCallback(() => {
    serviceRef.current?.connect();
  }, []);

  const disconnect = useCallback(() => {
    serviceRef.current?.disconnect();
    setIsConnected(false);
  }, []);

  const contextValue = useMemo<RabbitStompContextData>(
    () => ({
      isConnected,
      error,
      sendToQueue,
      subscribeToQueue,
      subscribeToTopic,
      connect,
      disconnect,
    }),
    [isConnected, error, sendToQueue, subscribeToQueue, subscribeToTopic, connect, disconnect]
  );

  return (
    <RabbitStompContext.Provider value={contextValue}>
      {children}
    </RabbitStompContext.Provider>
  );
};

export const useRabbitStompContext = (): RabbitStompContextData => {
  const context = useContext(RabbitStompContext);
  if (!context) {
    throw new Error('useRabbitStompContext deve ser usado dentro de RabbitStompProvider');
  }
  return context;
};

// Hook para usar uma instância já existente do RabbitStompService
export const useRabbitStompInstance = (): RabbitStompContextData => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInstanceAvailable, setIsInstanceAvailable] = useState(false);
  const serviceRef = useRef<RabbitStompService | null>(null);
  const subscriptionsRef = useRef<Map<string, StompSubscription>>(new Map());

  // Tentar obter instância existente
  useEffect(() => {
    console.log('🔌 [useRabbitStompInstance] Iniciando tentativa de obter instância...');
    
    const tryGetInstance = () => {
      try {
        console.log('🔌 [useRabbitStompInstance] Tentando obter instância do RabbitStompService...');
        serviceRef.current = RabbitStompService.getInstance();
        console.log('🔌 [useRabbitStompInstance] Instância obtida com sucesso!');
        setIsInstanceAvailable(true);
        return true;
      } catch (err) {
        console.log('🔌 [useRabbitStompInstance] Falha ao obter instância:', err);
        setIsInstanceAvailable(false);
        return false;
      }
    };

    // Tenta pegar a instância imediatamente
    if (tryGetInstance()) {
      const subscriptions = subscriptionsRef.current;
      const service = serviceRef.current;

      return () => {
        subscriptions.forEach((subscription) => {
          service?.unsubscribe(subscription);
        });
        subscriptions.clear();
      };
    }

    // Se não conseguiu, tenta novamente a cada 500ms
    console.log('🔌 [useRabbitStompInstance] Instância não disponível, iniciando polling...');
    const interval = setInterval(() => {
      console.log('🔌 [useRabbitStompInstance] Tentando obter instância novamente...');
      if (tryGetInstance()) {
        console.log('🔌 [useRabbitStompInstance] Instância obtida, parando polling');
        clearInterval(interval);
      }
    }, 500);

    const subscriptions = subscriptionsRef.current;
    const service = serviceRef.current;

    return () => {
      clearInterval(interval);
      subscriptions.forEach((subscription) => {
        service?.unsubscribe(subscription);
      });
      subscriptions.clear();
    };
  }, []);

  // Escutar eventos do service
  useEffect(() => {
    if (!isInstanceAvailable || !serviceRef.current) return;

    const handleConnect = () => {
      console.log('🔌 [useRabbitStompInstance] handleConnect chamado - setando isConnected para true');
      setIsConnected(true);
      setError(null);
    };

    const handleDisconnect = () => {
      console.log('🔌 [useRabbitStompInstance] handleDisconnect chamado - setando isConnected para false');
      setIsConnected(false);
    };

    const handleError = (errorMsg: string) => {
      console.log('🔌 [useRabbitStompInstance] handleError chamado:', errorMsg);
      setError(errorMsg);
      setIsConnected(false);
    };

    console.log('🔌 [useRabbitStompInstance] Registrando event handlers');
    serviceRef.current.on('connect', handleConnect);
    serviceRef.current.on('disconnect', handleDisconnect);
    serviceRef.current.on('error', handleError);

    // Verificar estado inicial imediatamente
    console.log('🔌 [useRabbitStompInstance] Verificando estado inicial...');
    const initialState = serviceRef.current.isConnected();
    console.log('🔌 [useRabbitStompInstance] Estado inicial da conexão:', initialState);
    setIsConnected(initialState);

    // Verificar novamente após um delay para capturar conexões que completaram logo após a montagem
    const recheckTimeout = setTimeout(() => {
      if (serviceRef.current) {
        console.log('🔌 [useRabbitStompInstance] Re-verificando estado após 500ms...');
        const currentState = serviceRef.current.isConnected();
        console.log('🔌 [useRabbitStompInstance] Estado da conexão após delay:', currentState);
        setIsConnected(currentState);
      }
    }, 500);

    // Polling para garantir sincronização do estado
    // Útil quando a conexão já estava estabelecida antes dos handlers serem registrados
    const checkInterval = setInterval(() => {
      if (serviceRef.current) {
        const currentState = serviceRef.current.isConnected();
        setIsConnected(prev => {
          if (prev !== currentState) {
            console.log('🔌 [useRabbitStompInstance] Estado da conexão mudou de', prev, 'para', currentState);
          }
          return currentState;
        });
      }
    }, 2000);

    return () => {
      clearTimeout(recheckTimeout);
      clearInterval(checkInterval);
      serviceRef.current?.off('connect', handleConnect);
      serviceRef.current?.off('disconnect', handleDisconnect);
      serviceRef.current?.off('error', handleError);
    };
  }, [isInstanceAvailable]);

  const sendToQueue = useCallback(
    async (message: string): Promise<void> => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }
      return serviceRef.current.sendToQueue(message);
    },
    []
  );

  const subscribeToQueue = useCallback(
    (queueName: string, callback: (message: string) => void): (() => void) => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }

      const subscription = serviceRef.current.subscribeToQueue(queueName, callback);
      subscriptionsRef.current.set(queueName, subscription);

      return () => {
        serviceRef.current?.unsubscribe(subscription);
        subscriptionsRef.current.delete(queueName);
      };
    },
    []
  );

  const subscribeToTopic = useCallback(
    (
      topicPattern: string,
      callback: (message: string, destination: string) => void
    ): (() => void) => {
      if (!serviceRef.current) {
        throw new Error('Service não está inicializado');
      }

      const subscription = serviceRef.current.subscribeToTopic(topicPattern, callback);
      const key = `topic_${topicPattern}`;
      subscriptionsRef.current.set(key, subscription);

      return () => {
        serviceRef.current?.unsubscribe(subscription);
        subscriptionsRef.current.delete(key);
      };
    },
    []
  );

  const connect = useCallback(() => {
    serviceRef.current?.connect();
  }, []);

  const disconnect = useCallback(() => {
    serviceRef.current?.disconnect();
    setIsConnected(false);
  }, []);

  return {
    isConnected,
    error,
    sendToQueue,
    subscribeToQueue,
    subscribeToTopic,
    connect,
    disconnect,
  };
};
